// The Audiotool session, for the whole app.
//
// Sign-in is a FULL-PAGE redirect: the page goes to Audiotool and comes back
// to this origin with a code. The origin must be registered on the Audiotool
// app EXACTLY as sent — see audiotoolRedirectUrl. The session start below
// redeems it wherever it lands — hence a provider at the root, not a hook per
// page — then returns to the page the user left.
//
// Not the SDK's audiotoolPopup(): in @audiotool/nexus 0.0.17 it drives
// accounts.audiotool.com/oauth/popup/start and /callback, and both answer 404
// on Audiotool's production site (checked 2026-09-24), so a popup can only
// ever show an error.
//
// The SDK is imported lazily so it stays out of the main bundle, and the
// session starts ONCE per page load: React's StrictMode runs effects twice in
// development, and a second audiotool() call would try to redeem an already
// used code.

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import type { AuthenticatedClient, BrowserAuthResult } from "@audiotool/nexus";
import { AUDIOTOOL_CLIENT_ID, AUDIOTOOL_SCOPE } from "@web-shared/lib/audiotool";
import { track } from "@web-shared/lib/analytics";

export type AudiotoolSession =
  | { status: "checking" }
  | { status: "signed-in"; client: AuthenticatedClient; userName: string }
  | { status: "signed-out"; error: string | null };

interface Ctx {
  session: AudiotoolSession;
  /** Leaves the page for Audiotool's sign-in; comes back to `returnTo`. */
  signIn: (returnTo: string) => void;
  signOut: () => void;
  /** The exact redirect URI that must be registered on the Audiotool app. */
  redirectUrl: string;
}

const AudiotoolContext = createContext<Ctx | null>(null);
const RETURN_KEY = "seedbed.audiotool.returnTo";

let started: Promise<BrowserAuthResult> | null = null;

/**
 * The redirect URI sent to Audiotool: the bare origin, NO trailing slash —
 * matched character for character against the app's registration, which is
 * `https://seedbed-viz.seed-bed.workers.dev` (checked 2026-09-24: the same URI
 * with a trailing slash is rejected before login). The browser lands on "/"
 * either way; the token exchange repeats this exact string.
 */
export function audiotoolRedirectUrl(): string {
  return window.location.origin;
}

function start(): Promise<BrowserAuthResult> {
  started ??= import("@audiotool/nexus").then((m) =>
    m.audiotool({ clientId: AUDIOTOOL_CLIENT_ID, redirectUrl: audiotoolRedirectUrl(), scope: AUDIOTOOL_SCOPE }),
  );
  return started;
}

export function AudiotoolProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [session, setSession] = useState<AudiotoolSession>({ status: "checking" });
  const [login, setLogin] = useState<(() => void) | null>(null);

  const adopt = useCallback((r: BrowserAuthResult) => {
    if (r.status === "authenticated") {
      setSession({ status: "signed-in", client: r, userName: r.userName });
      setLogin(null);
    } else {
      setSession({ status: "signed-out", error: r.error ? r.error.message : null });
      setLogin(() => r.login);
    }
  }, []);

  useEffect(() => {
    let live = true;
    start()
      .then((r) => {
        if (!live) return;
        adopt(r);
        const back = sessionStorage.getItem(RETURN_KEY);
        if (back) {
          sessionStorage.removeItem(RETURN_KEY);
          if (r.status === "authenticated") track("audiotool_connected");
          navigate(back, { replace: true });
        }
      })
      .catch((e: unknown) => live && setSession({ status: "signed-out", error: e instanceof Error ? e.message : String(e) }));
    return () => {
      live = false;
    };
  }, [adopt, navigate]);

  const signIn = useCallback(
    (returnTo: string) => {
      sessionStorage.setItem(RETURN_KEY, returnTo);
      login?.();
    },
    [login],
  );

  const signOut = useCallback(() => {
    if (session.status === "signed-in") session.client.logout();
    started = null;
    setSession({ status: "checking" });
    void start().then(adopt);
  }, [session, adopt]);

  const value = useMemo(
    () => ({ session, signIn, signOut, redirectUrl: audiotoolRedirectUrl() }),
    [session, signIn, signOut],
  );
  return <AudiotoolContext.Provider value={value}>{children}</AudiotoolContext.Provider>;
}

export function useAudiotool(): Ctx {
  const ctx = useContext(AudiotoolContext);
  if (!ctx) throw new Error("useAudiotool outside <AudiotoolProvider>");
  return ctx;
}
